import React from 'react';
import './Pages.css';

const FAQ: React.FC = () => {
  const faqs = [
    {
      question: "What is GET AI PORTRAIT?",
      answer: "GET AI PORTRAIT is a premium iOS app that transforms your selfies into stunning AI-generated portraits across 56+ artistic styles. Using state-of-the-art AI models (FLUX 1.1 Pro, DALL-E 3, Stable Diffusion), the app delivers Hollywood-quality results in under 60 seconds."
    },
    {
      question: "How does it work?",
      answer: "Upload 5-10 selfies, select from 56+ artistic styles (Hollywood glamour, cyberpunk, fantasy renaissance, business professional, etc.), and our AI generates stunning portraits in under 60 seconds. You can preview watermarked versions for free before unlocking full-resolution portraits."
    },
    {
      question: "How many photos should I upload?",
      answer: "We recommend uploading 5-10 clear selfies for optimal results. More photos with variety (different angles, expressions, and lighting) help the AI create better portraits that capture your unique features."
    },
    {
      question: "How long does processing take?",
      answer: "Preview portraits are generated in under 60 seconds per style. Full-resolution portraits are available immediately after premium unlock. Background processing ensures portraits are ready even if you close the app."
    },
    {
      question: "What's the difference between free and paid?",
      answer: "Free users can preview all 56+ styles with watermarks. Premium unlock (€9.99 per portrait pack) provides full-resolution downloads without watermarks. Subscription (€3.99/month) offers unlimited portrait generation across all styles."
    },
    {
      question: "How much does it cost?",
      answer: "GET AI PORTRAIT offers flexible pricing: Premium Unlock: €9.99 per portrait pack (one-time purchase, full resolution, no watermark). Subscription: €3.99/month for unlimited portrait generation. Style Packs: Additional styles at €2.99 each. All pricing includes access to state-of-the-art AI models and CloudKit sync."
    },
    {
      question: "Can I cancel my subscription?",
      answer: "Yes, you can cancel your subscription anytime in your Apple Settings → Apple ID → Subscriptions. There's no cancellation fee. After canceling, you'll have access to premium features until the end of your paid period."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes! All users can generate watermarked preview portraits for free to try different styles before purchasing. This \"try-before-buy\" approach ensures you're satisfied with results before unlocking full-resolution portraits."
    },
    {
      question: "What portrait styles are available?",
      answer: "We offer 56+ unique styles including: Hollywood & Cinematic (Cinematic Glow, Vintage Hollywood, Film Noir). Artistic (Neo-Pop Art, Fantasy Renaissance, Oil Painting). Professional (Business Professional, Corporate Headshots, LinkedIn Ready). Creative (Cyberpunk Samurai, Steampunk, Gothic). Fashion & Beauty (High Fashion, Editorial, Glamour). And many more! New styles added regularly."
    },
    {
      question: "Can I use portraits commercially?",
      answer: "Yes! Once purchased, you have full ownership of your AI-generated portraits and may use them for: Social media profiles (Instagram, TikTok, LinkedIn). Dating apps (Tinder, Bumble, Hinge). Business cards and marketing materials. Professional headshots. Website and portfolio. Any personal or commercial purpose. Note: AI-generated content may not be copyrightable in all jurisdictions."
    },
    {
      question: "Is my data safe? (GDPR compliance)",
      answer: "Absolutely. GET AI PORTRAIT is fully GDPR and EU AI Act compliant: Face detection for artistic transformation only (NOT facial recognition). Biometric data deleted immediately after portrait generation. Photos deleted from servers within 24 hours. End-to-end encryption for all uploads. CloudKit sync uses Apple's privacy infrastructure. No facial recognition profiles or databases created. You can request full data deletion anytime. See our Privacy Policy for complete details."
    },
    {
      question: "What happens to my uploaded photos?",
      answer: "Your privacy is our priority: Photos encrypted during upload. Processed temporarily for portrait generation. Deleted from servers within 24 hours. Biometric landmarks deleted immediately after processing. Generated portraits stored locally or synced via CloudKit. You control all data through the app settings."
    },
    {
      question: "Are portraits clearly labeled as AI-generated?",
      answer: "Yes, in compliance with the EU AI Act: Watermarked previews include \"AI Generated\" labels. Full-resolution portraits include metadata indicating AI generation. We provide transparency about AI model capabilities. All portraits are clearly identified as artistic interpretations. When sharing publicly, please use #AIGenerated or #AIArt hashtags."
    },
    {
      question: "What quality can I expect?",
      answer: "GET AI PORTRAIT delivers professional-quality results: Full HD output (2048x2048 pixels). Identity preservation threshold: 0.4-0.5 similarity score. Face detection accuracy &gt; 95% (Apple Vision Framework). Human-rated quality average: 4.2/5 stars. Trained on diverse datasets for all skin tones and face types. Note: AI results may vary based on input photo quality."
    },
    {
      question: "What if I'm not satisfied with results?",
      answer: "We offer a 7-day satisfaction guarantee. If you're not happy with your portraits, request a refund through Apple's App Store (reportaproblem.apple.com) or contact our support team at support@dreif-ai.com. See our Refund Policy for details."
    },
    {
      question: "Can I request specific styles?",
      answer: "Yes! We're always adding new styles based on user feedback. Email your style suggestions to support@dreif-ai.com. Popular requests are prioritized for future releases."
    },
    {
      question: "What formats are portraits available in?",
      answer: "Portraits are delivered in high-resolution formats suitable for both digital use and printing: PNG format (lossless, transparent backgrounds where applicable). Full HD resolution (2048x2048 pixels). JPEG export available via native iOS sharing. PDF export for professional printing."
    },
    {
      question: "Can I share portraits on social media?",
      answer: "Absolutely! GET AI PORTRAIT includes native sharing features: One-tap sharing to Instagram, TikTok, Facebook, Twitter. Pre-formatted posts with #GetAIPortrait hashtags. Viral templates for maximum engagement. Direct download to Camera Roll. Please disclose AI generation when sharing publicly (EU AI Act requirement)."
    },
    {
      question: "Do I need an internet connection?",
      answer: "Yes, for portrait generation. AI processing happens via cloud-based models (Replicate, OpenAI APIs) for the highest quality results. However, you can browse your gallery and view saved portraits offline. CloudKit sync requires internet but works seamlessly in the background."
    },
    {
      question: "Which iOS devices are supported?",
      answer: "GET AI PORTRAIT requires: iOS 16.0 or later. iPhone or iPad. Apple ID for CloudKit sync (optional). Internet connection for portrait generation. The app is optimized for all iPhone models (iPhone 12 and newer recommended for best performance)."
    },
    {
      question: "Can I use GET AI PORTRAIT on Android?",
      answer: "Currently, GET AI PORTRAIT is iOS-exclusive (built with native SwiftUI and Core ML). An Android version is not planned at this time, as we're focused on delivering the best iOS experience."
    },
    {
      question: "How is GET AI PORTRAIT different from other AI portrait apps?",
      answer: "GET AI PORTRAIT stands out with: Latest AI models (FLUX 1.1 Pro, DALL-E 3) for superior quality. 56+ unique styles (more than most competitors). Under 60-second processing (fastest in the industry). Try-before-buy previews (no blind purchases). GDPR & EU AI Act compliant (privacy-first design). Native iOS with CloudKit sync (seamless experience). Professional-quality output (2048x2048 Full HD). Human-rated quality control (&gt; 4.2/5 average)."
    },
    {
      question: "Does GET AI PORTRAIT use facial recognition?",
      answer: "No. We use face detection for artistic transformation only, NOT facial recognition: Apple Vision Framework detects faces for portrait framing. Facial landmarks extracted temporarily for style application. No biometric templates or profiles created. No identification or tracking of individuals. All biometric data deleted immediately after generation. Full transparency and GDPR Article 9 compliance. See our Privacy Policy for technical details."
    },
    {
      question: "Can I delete my account and data?",
      answer: "Yes, you have full control over your data: Delete account and all data via app settings. Request data deletion by emailing privacy@dreif-ai.com. All photos, portraits, and metadata permanently deleted. Compliance with GDPR Right to Erasure (Art. 17). See our Data Deletion page for step-by-step instructions."
    },
    {
      question: "Are there age restrictions?",
      answer: "Yes. GET AI PORTRAIT is intended for users 18 years or older. We do not knowingly process biometric data of minors. Parental consent required for users under 18. If underage use is discovered, all data is immediately deleted."
    },
    {
      question: "How do I contact support?",
      answer: "We're here to help! General inquiries: support@dreif-ai.com. Privacy & data protection: privacy@dreif-ai.com. Press & media: press@dreif-ai.com. We typically respond within 24 hours."
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Frequently Asked Questions</h1>
        <p className="page-subtitle">Everything you need to know about GET AI PORTRAIT</p>
      </div>

      <div className="page-content">
        {faqs.map((faq, index) => (
          <div
            key={index}
            style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '10px',
              marginBottom: '1.5rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #0AD4CF'
            }}
          >
            <h3 style={{ fontSize: '1.3rem', marginBottom: '0.75rem', color: '#1a1a1a' }}>
              {faq.question}
            </h3>
            <p style={{ margin: 0, color: '#666', lineHeight: '1.6' }}>
              {faq.answer}
            </p>
          </div>
        ))}

        <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '10px', marginTop: '3rem', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0AD4CF' }}>
            Still Have Questions?
          </h3>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            If you didn't find your answer here, our support team is ready to help!
          </p>
          <a
            href="/support"
            style={{
              display: 'inline-block',
              backgroundColor: '#0AD4CF',
              color: 'white',
              padding: '12px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              marginRight: '10px'
            }}
          >
            📧 Contact Support
          </a>
          <a
            href="mailto:support@dreif-ai.com"
            style={{
              display: 'inline-block',
              backgroundColor: '#0A9399',
              color: 'white',
              padding: '12px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            ✉️ Email Us
          </a>
        </div>

        <div style={{ marginTop: '3rem', padding: '2rem', backgroundColor: '#f0f9ff', borderRadius: '10px' }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#1a1a1a' }}>
            📚 Additional Resources
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ marginBottom: '0.5rem' }}>
              <a href="/privacy" style={{ color: '#0AD4CF', textDecoration: 'none' }}>
                🔒 Privacy Policy
              </a>
              {' - '} How we protect your data
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <a href="/terms" style={{ color: '#0AD4CF', textDecoration: 'none' }}>
                📄 Terms of Service
              </a>
              {' - '} Usage terms and conditions
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <a href="/ai-disclosure" style={{ color: '#0AD4CF', textDecoration: 'none' }}>
                🤖 AI Disclosure
              </a>
              {' - '} How our AI technology works
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <a href="/data-deletion" style={{ color: '#0AD4CF', textDecoration: 'none' }}>
                🗑️ Data Deletion
              </a>
              {' - '} How to delete your data
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <a href="/refund-policy" style={{ color: '#0AD4CF', textDecoration: 'none' }}>
                💰 Refund Policy
              </a>
              {' - '} Our satisfaction guarantee
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
